import { all, call, put, takeLatest } from "redux-saga/effects";
import {
  startDubbingRequest,
  setUploadStep,
  setUploading,
  setCreatingJob,
  setRedirectingToCheckout,
  startDubbingSuccess,
  startDubbingFailure,
} from "../Redux/DubbingRedux";

/**
 * Start Dubbing Flow:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to S3
 * 3. Create dubbing job with the s3_key
 */
function* onStartDubbing(api, action) {
  const { mediaFile, mediaType, config, callback } = action.payload;

  try {
    // --- Step 1: Get presigned upload URL ---
    yield put(setUploading(true));
    yield put(setUploadStep("presigning"));

    const contentType = mediaFile.type || (mediaType === "video" ? "video/mp4" : "audio/mpeg");

    const presignResp = yield call(api.presignUpload, {
      filename: mediaFile.name,
      content_type: contentType,
      byte_size: mediaFile.size,
    });

    if (!presignResp.ok) {
      const errMsg = presignResp.data?.error || "Failed to get upload URL";
      yield put(startDubbingFailure(errMsg));
      if (callback) callback(null, errMsg);
      return;
    }

    const { presigned_url, s3_key, headers } = presignResp.data;

    // --- Step 2: Upload file to S3 ---
    yield put(setUploadStep("uploading"));

    const uploadResp = yield call(api.uploadToS3, presigned_url, mediaFile, headers["Content-Type"]);

    if (!uploadResp.ok) {
      const errMsg = "Failed to upload file to S3";
      yield put(startDubbingFailure(errMsg));
      if (callback) callback(null, errMsg);
      return;
    }

    // --- Step 3: Create dubbing job ---
    yield put(setUploadStep("creating"));
    yield put(setUploading(false));
    yield put(setCreatingJob(true));

    const jobResp = yield call(api.createDubbingJob, {
      dubbing_job: {
        s3_key: s3_key,
        input_type: mediaType,
        target_languages: config.targetLanguages,
        voice_mode: config.voiceMode,
        lip_sync: config.lipSync,
        subtitles: config.subtitles,
        title: mediaFile.name.replace(/\.[^/.]+$/, ""),
        duration_seconds: config.durationSeconds,
      },
    });

    if (!jobResp.ok) {
      const errMsg =
        jobResp.data?.error ||
        jobResp.data?.errors?.join(", ") ||
        "Failed to create dubbing job";
      yield put(startDubbingFailure(errMsg));
      if (callback) callback(null, errMsg);
      return;
    }

    // --- Check if payment is required ---
    if (jobResp.data.needs_payment && jobResp.data.checkout_url) {
      yield put(setCreatingJob(false));
      yield put(setRedirectingToCheckout(true));
      yield put(setUploadStep("redirecting"));
      // Redirect to Stripe Checkout
      window.location.href = jobResp.data.checkout_url;
      return;
    }

    // --- Success (credits were sufficient) ---
    yield put(startDubbingSuccess(jobResp.data));
    if (callback) callback(jobResp.data, null);
  } catch (e) {
    const errMsg = e.message || "An unexpected error occurred";
    yield put(startDubbingFailure(errMsg));
    if (callback) callback(null, errMsg);
  }
}

export default function* dubbingSagas(api) {
  yield all([
    takeLatest(startDubbingRequest.type, onStartDubbing, api),
  ]);
}
